import {useEffect, useState} from 'react';
import {useSession} from "../../firebase/UserProvider";
import {nanoid} from "nanoid";
import {apiCreateTag, apiDeleteTag} from "../../utils/api/tags";

const normalizeTag = (t) => ({
    name: (t?.name || t?.tag || '').toString(),
    voteCount: typeof t?.voteCount === 'number' ? Number(t.voteCount) : 0,
    operationId: t?.operationId ?? null,
});

const tagsEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
        const na = normalizeTag(a[i]);
        const nb = normalizeTag(b[i]);
        if (na.name.trim().toLowerCase() !== nb.name.trim().toLowerCase()) return false;
        if ((na.voteCount || 0) !== (nb.voteCount || 0)) return false;
        if ((na.operationId || null) !== (nb.operationId || null)) return false;
    }
    return true;
};

/**
 * Hook to manage tags for a specific segment (docId + segmentId + offset).
 * Mirrors useDocumentTags, but sends entityType: 'SEGMENT' with entity identifiers.
 */
export default function useSegmentTags({docId, segmentTags, segmentId, segmentIndex, offset}) {
    const {user} = useSession();
    const [tags, setTags] = useState(Array.isArray(segmentTags) ? segmentTags : []);
    const [isPosting, setIsPosting] = useState(false);
    const [loadingTagOps, setLoadingTagOps] = useState({});
    const normalizedSegmentIndex = Number.isFinite(Number(segmentIndex)) ? Number(segmentIndex) : null;

    useEffect(() => {
        if (!Array.isArray(segmentTags)) return;
        // keep local state in sync when the parent passes updated segmentTags
        const incoming = segmentTags;
        setTags((prev) => {
            if (tagsEqual(prev, incoming)) return prev;
            try {
                // eslint-disable-next-line no-console
                console.warn('[useSegmentTags] incoming tags changed for segmentId=', segmentId, 'docId=', docId);
            } catch (_) {
            }
            return incoming;
        });
    }, [segmentTags, segmentId, docId]);

    function findTagIndex(list, tag) {
        const t = (tag || '').trim().toLowerCase();
        return list.findIndex(x => ((x.tag || x.name) || '').toString().trim().toLowerCase() === t);
    }

    // Create a tag (also counts as user's vote)
    async function createTag(tag, newOperationId) {
        const tRaw = (tag || '').trim().toLowerCase();
        const hasSegmentLocator = segmentId != null || normalizedSegmentIndex != null;
        if (!tRaw || docId == null || !hasSegmentLocator) return;
        setIsPosting(true);
        const tLower = tRaw.toLowerCase();
        try {
            const operationId = newOperationId ? newOperationId : nanoid();
            const entity = {docId};
            if (segmentId != null) entity.segmentId = segmentId;
            if (normalizedSegmentIndex != null) entity.segmentIndex = normalizedSegmentIndex;
            if (offset != null) entity.offset = offset;
            const reqObj = {
                operationId,
                name: tRaw,
                entityType: "SEGMENT",
                entity,
                userId: user?.uid,
            };
            // optimistic add if not present
            setTags(prev => {
                const current = Array.isArray(prev) ? prev : [];
                if (current.some(x => (x.name || '').toLowerCase() === tLower)) return current;
                return [{name: tRaw, voteCount: 1, operationId}, ...current];
            });
            await apiCreateTag(reqObj);
        } catch (e) {
            // rollback
            setTags(prev => (Array.isArray(prev) ? prev : []).filter(x => (x.name || '').toLowerCase() !== tLower));
            throw e;
        } finally {
            setIsPosting(false);
        }
    }

    async function voteTag(tag) {
        console.log(tag)
        const tRaw = (tag || '').trim();
        const hasSegmentLocator = segmentId != null || normalizedSegmentIndex != null;
        if (!tRaw || docId == null || !hasSegmentLocator) return;
        const tLower = tRaw.toLowerCase();
        const currentTag = tags.find(x => (x.name || '').toLowerCase() === tLower);
        const existingOperationId = currentTag?.operationId ?? null;
        if (loadingTagOps[tLower]) return;
        setLoadingTagOps(prev => ({...prev, [tLower]: true}));

        let prevTags = null;
        try {
            prevTags = Array.isArray(tags) ? tags.map(x => ({...x})) : [];
            const alreadyVoted = !!existingOperationId;
            // optimistic state
            setTags(prev => {
                const current = Array.isArray(prev) ? prev.map(x => ({...x})) : [];
                const idx = findTagIndex(current, tRaw);
                if (idx === -1) {
                    if (!alreadyVoted) {
                        // delegate to createTag later
                        return current;
                    }
                    return current;
                }
                const item = {...current[idx]};
                const count = typeof item.voteCount === 'number' ? item.voteCount : 0;
                if (alreadyVoted) {
                    item.operationId = null;
                    item.voteCount = Math.max(0, count - 1);
                    if (item.voteCount === 0) {
                        current.splice(idx, 1);
                        return current;
                    }
                    current[idx] = item;
                    return current;
                } else {
                    item.voteCount = count + 1;
                    current[idx] = item;
                    return current;
                }
            });

            if (!alreadyVoted) {
                const newOperationId = nanoid();
                // set operationId optimistically
                setTags(prev => {
                    const current = Array.isArray(prev) ? prev.map(x => ({...x})) : [];
                    const idx = findTagIndex(current, tRaw);
                    if (idx === -1) return current;
                    const n = current;
                    n[idx] = {...n[idx], operationId: newOperationId};
                    return n;
                });
                return await createTag(tRaw, newOperationId);
            } else {
                if (!existingOperationId) {
                    setTags(prevTags);
                    return Promise.reject(new Error('No operationId to remove vote'));
                }
                return await removeTag(existingOperationId, tRaw);
            }
        } catch (e) {
            if (Array.isArray(prevTags)) setTags(prevTags);
            throw e;
        } finally {
            setLoadingTagOps(prev => ({...prev, [tLower]: false}));
        }
    }

    async function removeTag(operationId, tagName) {
        if (!operationId) throw new Error('operationId is required');
        const tRaw = (tagName || '').trim();
        const tLower = tRaw.toLowerCase();
        if (tRaw && loadingTagOps[tLower]) return;
        if (tRaw) setLoadingTagOps(prev => ({...prev, [tLower]: true}));
        try {
            const resp = await apiDeleteTag(operationId);
            // backend may return JSON with voteCount; try to parse
            let serverVotes = 0;
            try {
                if (resp && typeof resp.json === 'function') {
                    const data = await resp.json();
                    if (typeof data?.voteCount === 'number') serverVotes = Number(data.voteCount);
                }
            } catch (_) {
            }
            if (tRaw) {
                if (serverVotes > 0) {
                    setTags(prev => {
                        const idx = findTagIndex(prev, tRaw);
                        if (idx !== -1) {
                            return prev.map(x => (x.name || '').toLowerCase() === tLower ? {
                                name: tRaw,
                                voteCount: serverVotes,
                                operationId: null
                            } : x);
                        }
                        return [{name: tRaw, voteCount: serverVotes, operationId: null}, ...prev];
                    });
                }
            }
            return resp;
        } finally {
            if (tRaw) setLoadingTagOps(prev => ({...prev, [tLower]: false}));
        }
    }

    return {tags, createTag, voteTag, removeTag, isPosting, loadingTagOps};
}
